import { CodeResponse, useGoogleLogin } from "@react-oauth/google";
import * as React from "react";
import LocalStorageService from "../../auth/local-storage-service";
import { rememberAccount } from "../../auth/remembered-accounts";
import { UserService } from "../../services/user-service";
import { KNavigator, withHistory } from "../../shared/hoc/with-history";
import KuikaButton from "../ui-kit/kuika-button/kuika-button";
import KuikaTextMedium from "../ui-kit/typography/TextMedium/TextMedium";

interface IHooksHOCProps {
  navigator: KNavigator;
}

export interface IGoogleOneTapProps {
  routing: "dynamic" | "static";
  type: "signup" | "login";
  onClick: () => void;
  onSuccess: () => void;
  onFailure?: (error?: string) => void;
  loading?: boolean;
  /** Pre-selects this address in Google's account picker (`login_hint`). */
  hint?: string;
  /**
   * `primary` when Google is the way this person signed in last time: the returning-account step
   * leads with it. Everywhere else it is one option among several and stays the bordered button.
   */
  emphasis?: "default" | "primary";
}

type Props = IGoogleOneTapProps & IHooksHOCProps;

declare let window: Window & { [key: string]: any };

/**
 * Google sign-in is offered only where the OAuth client allows the origin: local development and
 * kuika.com. Elsewhere (on-prem, customer domains) the button would open a popup that fails.
 */
export const isGoogleSignInAvailable = (): boolean =>
  window.location.origin.includes("localhost") || window.location.origin.includes("kuika.com");

class GoogleOneTap extends React.Component<Props> {
  render() {
    const isPrimary: boolean = this.props.emphasis === "primary";
    return (
      // A bordered neutral button is a kit variant: `secondary` + `neutral` already draws the
      // hairline in `--control-border` and the label in `--dark-7`, and the lg rung brings its own
      // radius. Hand-rolling those inline gave the sign-in page a 4px corner and a `--dark-5`
      // border — a step off the control border everywhere else on the form.
      <KuikaButton
        type={isPrimary ? "primary" : "secondary"}
        color={isPrimary ? "default" : "neutral"}
        size="lg"
        className="kuika-width-full"
        onClick={this.props.onClick}
        loading={this.props.loading}
        disabled={this.props.loading}
        data-testid="googleSignInButton"
      >
        <div className="auth-page__google-btn-content">
          {/* On the blue primary fill the multicolour mark needs its own light tile to stay legible. */}
          <span
            className={isPrimary ? "auth-page__google-mark auth-page__google-mark--tiled" : "auth-page__google-mark"}
          >
            <img src="images/common/google.svg" width={18} height={18} alt="" />
          </span>
          <KuikaTextMedium>
            {this.props.type === "signup" ? "Sign up with Google" : "Continue with Google"}
          </KuikaTextMedium>
        </div>
      </KuikaButton>
    );
  }
}

const WrappedComponent = (props: Props) => {
  const onClickGoogleOneTap = useGoogleLogin({
    onSuccess: (codeResponse) => handleOnSuccessLogin(codeResponse, props),
    flow: "auth-code",
    hint: props.hint
  });

  const handleOnSuccessLogin = async (codeResponse: CodeResponse, props: Props) => {
    props.onSuccess();
    LocalStorageService.clearToken();
    window.userInfo = undefined;
    UserService.LoginWithGoogle({ AuthCode: codeResponse.code })
      .then(async ({ data }) => {
        if (data && data.tokenString) {
          LocalStorageService.setToken(data);
          try {
            await UserService.GetUserInfo();
            LocalStorageService.setAuthenticatedWithCustomProvider("google");
            // Remembered at the sign-in site, like the password path — see `rememberAccount`.
            rememberAccount(window.userInfo, "google");

            if (props.routing === "static") {
              window.skipAlert = true;
              window.location.href = "/";
            } else {
              props.navigator.gotoRecent();
            }
          } catch (error) {
            const msg = "Login failed: Unable to retrieve user info.";
            if (props.onFailure) {
              props.onFailure(msg);
            }
          }
        } else {
          const msg = "Login failed: No token received.";
          if (props.onFailure) {
            props.onFailure(msg);
          }
        }
      })
      .catch((error) => {
        const msg = error.response?.data?.message || error.message;
        if (props.onFailure) {
          props.onFailure(msg);
        }
      });
  };

  return (
    <>
      <GoogleOneTap {...props} onClick={onClickGoogleOneTap} />
    </>
  );
};

/** What a caller passes: `onClick` is wired here and `navigator` comes from `withHistory`. */
export type GoogleLoginProps = Omit<IGoogleOneTapProps, "onClick">;

const googleLogin = withHistory<GoogleLoginProps>(WrappedComponent);
export { googleLogin as GoogleLogin };
